import { Action, createReducer, on } from '@ngrx/store';
import {
  clearGetAllAccountPointsV2Action,
  getAllAccountPointsAction,
  getAllAccountPointsBulkAssignAccountsAction,
  getAllAccountPointsBulkAssignCategoryAction,
  getAllAccountPointsBulkAssignTagsAction,
  getAllAccountPointsBulkRemoveAccountsAction,
  getAllAccountPointsBulkRemoveTagsAction,
  getAllAccountPointsBulkUpdateAccountsTypeAction,
  getAllAccountPointsFailureAction,
  getAllAccountPointsSuccessAction,
} from './get-all-account-points.actions';
import { GetAllAccountPointsState } from './get-all-account-points.types';
import { AccountPointSchema } from '../../../models/account';
import { errorState, getStateByContext, initialState, loadedState, loadingState } from '../../../utils/ngrx-state';
import { union } from 'lodash';

const applyToAllContexts = (
  state: GetAllAccountPointsState,
  mapper: (points: AccountPointSchema[]) => AccountPointSchema[],
): GetAllAccountPointsState => {
  const result = { ...state };
  for (const key of Object.keys(state)) {
    const ctx = state[key];
    if (ctx?.data) {
      result[key] = { ...ctx, data: mapper(ctx.data) };
    }
  }
  return result;
};

const getAllAccountPointsReducer = createReducer<GetAllAccountPointsState>(
  initialState(),
  on(
    getAllAccountPointsAction,
    (state, action) =>
      loadingState(state, action.context, action.refresh)
  ),
  on(
    getAllAccountPointsSuccessAction,
    (state, action) => {
      const ctx = action.context ?? 'default';
      const currentData: AccountPointSchema[] = getStateByContext<AccountPointSchema[], string>(state, ctx)?.data ?? [];
      return loadedState([ ...currentData, ...action.accountPoints ], state, ctx);
    }
  ),
  on(
    getAllAccountPointsFailureAction,
    (state, action) =>
      errorState(action.error, state, action.context)
  ),
  on(
    clearGetAllAccountPointsV2Action,
    () => initialState()
  ),
  on(
    getAllAccountPointsBulkAssignTagsAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.map(a =>
          action.payload.accounts.some(pa => pa.id === a.fbId)
            ? { ...a, tags: union(a.tags, action.payload.tags) }
            : a
        )
      )
  ),
  on(
    getAllAccountPointsBulkRemoveTagsAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.map(a =>
          action.payload.accounts.some(pa => pa.id === a.fbId) && a.tags
            ? { ...a, tags: a.tags.filter(t => !action.payload.tags.includes(t)) }
            : a
        )
      )
  ),
  on(
    getAllAccountPointsBulkAssignCategoryAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.map(a =>
          action.payload.accounts.some(pa => pa.id === a.fbId)
            ? { ...a, accountType: action.payload.category }
            : a
        )
      )
  ),
  on(
    getAllAccountPointsBulkUpdateAccountsTypeAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.map(a =>
          action.payload.ids.includes(a.fbId)
            ? { ...a, scope: action.payload.scope }
            : a
        )
      )
  ),
  on(
    getAllAccountPointsBulkAssignAccountsAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.map(a => {
          const relatedPayload = action.payload.find(p => p.id === a.fbId);
          if (!relatedPayload) return a;
          return {
            ...a,
            users: relatedPayload.users,
            ...(relatedPayload.primaryUserId === undefined ? {} : { primaryUserId: relatedPayload.primaryUserId }),
          };
        })
      )
  ),
  on(
    getAllAccountPointsBulkRemoveAccountsAction,
    (state, action) =>
      applyToAllContexts(state, points =>
        points.filter(a => !action.payload.accounts.some(pa => pa.id === a.fbId))
      )
  ),
);

export const reducer = (state: GetAllAccountPointsState, action: Action) => getAllAccountPointsReducer(state, action);
